import React from 'react';
import { GitBranch, Shuffle, Target, Timer } from 'lucide-react';
import { AutomationNodeType, AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import { Button } from '@/shadcn/components/ui/button';
import { NodeTemplate, NodeCategory } from '../types';

interface NodeOptionBarProps {
  parentNodeId: string;
  onAddNode: (parentId: string, template: NodeTemplate, isElseBranch?: boolean, isBranch?: boolean) => void;
  onClose: () => void;
  position: { x: number; y: number };
  isElseBranch?: boolean;
}

export const NodeOptionBar: React.FC<NodeOptionBarProps> = ({
  parentNodeId,
  onAddNode,
  onClose,
  position,
  isElseBranch = false,
}) => {
  const handleAddDelay = () => {
    onAddNode(parentNodeId, {
      type: AutomationNodeType.DELAY,
      category: NodeCategory.UTILITY,
      displayName: 'Wait',
      description: 'Wait for a specified time',
      icon: 'clock',
      defaultMetadata: { delayAmount: 1, delayUnit: 'hours' },
      allowsChildren: true,
    }, isElseBranch, false); // false = inline insertion
    onClose();
  };

  const handleAddCondition = () => {
    onAddNode(parentNodeId, {
      type: AutomationNodeType.CONDITION,
      category: NodeCategory.LOGIC,
      displayName: 'Condition',
      description: 'Check a condition',
      icon: 'check',
      defaultMetadata: {},
      allowsChildren: true,
      maxChildren: 2,
    }, isElseBranch, false); // false = inline insertion
    onClose();
  };

  const handleAddAction = () => {
    onAddNode(parentNodeId, {
      type: AutomationNodeType.ACTION,
      subType: AutomationActionNodeSubType.SEND_EMAIL,
      category: NodeCategory.ACTION,
      displayName: 'Send Email',
      description: 'Send an email',
      icon: 'mail',
      defaultMetadata: { subType: AutomationActionNodeSubType.SEND_EMAIL },
      allowsChildren: false,
    }, isElseBranch, false); // false = inline insertion
    onClose();
  };

  const handleAddPath = () => {
    // Create a placeholder node that will show selection UI
    onAddNode(parentNodeId, {
      type: AutomationNodeType.ACTION, // Use ACTION as base type
      subType: undefined, // No subType = placeholder state
      category: NodeCategory.PLACEHOLDER,
      displayName: 'Select Block',
      description: 'Choose which type of block to add',
      icon: 'plus',
      defaultMetadata: { isPlaceholder: true },
      allowsChildren: true,
    }, isElseBranch, true); // true = branch creation
    onClose();
  };

  // Handle clicks outside to close
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target && !target.closest('[data-node-option-bar]')) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div
      data-node-option-bar
      className="absolute z-50 bg-gray-50 border border-gray-200 rounded-full shadow-lg px-2 py-1 flex items-center gap-1"
      style={{
        left: position.x - 120, // Center the horizontal pill
        top: position.y + 10,
      }}
    >
      <Button
        variant="ghost"
        size="sm"
        className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-white rounded-full min-w-[80px] h-8"
        onClick={handleAddCondition}
      >
        <GitBranch className="h-4 w-4 text-gray-600" />
        <span className="text-gray-700 font-medium">Condition</span>
      </Button>

      <div className="w-px h-6 bg-gray-300" /> {/* Divider */}

      <Button
        variant="ghost"
        size="sm"
        className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-white rounded-full min-w-[60px] h-8"
        onClick={handleAddDelay}
      >
        <Timer className="h-4 w-4 text-gray-600" />
        <span className="text-gray-700 font-medium">Delay</span>
      </Button>

      <div className="w-px h-6 bg-gray-300" /> {/* Divider */}

      <Button
        variant="ghost"
        size="sm"
        className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-white rounded-full min-w-[60px] h-8"
        onClick={handleAddAction}
      >
        <Target className="h-4 w-4 text-gray-600" />
        <span className="text-gray-700 font-medium">Action</span>
      </Button>

      <div className="w-px h-6 bg-gray-300" /> {/* Divider */}

      <Button
        variant="ghost"
        size="sm"
        className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-white rounded-full min-w-[75px] h-8"
        onClick={handleAddPath}
      >
        <Shuffle className="h-4 w-4 text-gray-600" />
        <span className="text-gray-700 font-medium">Add Path</span>
      </Button>
    </div>
  );
};
